import type { EnemyState, ProjectileState, PowerUpState, PowerUpKind, GameStatus } from './types';
import type { Vec3 } from './math/vec3';

// The single seam between the simulation (core) and everything reactive
// (rendering, audio, UI, networking). Core emits; adapters subscribe. Nothing
// in core imports Three.js or the DOM, so the same events can drive a server.
export type GameEventMap = {
  started: { seed: number };
  reset: void;
  statusChanged: { status: GameStatus };
  paused: { paused: boolean };

  enemySpawned: { enemy: EnemyState };
  /** Correct answer: enemy removed, points awarded. Carries the mesh's last
   *  known state so the render layer can stage a death effect. */
  enemyDestroyed: { enemy: EnemyState; projectile: ProjectileState };
  /** Wrong answer struck an enemy; projectile is consumed, enemy survives. */
  answerWrong: { enemy: EnemyState; projectile: ProjectileState };

  projectileFired: { projectile: ProjectileState };
  weaponChanged: { weapon: number };
  scoreChanged: { score: number; delta: number };
  /** Correct-answer streak moved (up on hits, reset on misses). */
  comboChanged: { combo: number; multiplier: number };
  powerUpSpawned: { powerup: PowerUpState };
  powerUpCollected: { kind: PowerUpKind; position: Vec3 };
  /** Difficulty level ticked up (levels are time-based; see DIFFICULTY). */
  levelChanged: { level: number };
  /** A curriculum-ladder mode advanced to a new exercise: the board clears,
   *  pace eases back, and the UI announces the new task. */
  stageChanged: { ruleId: string; label: string; instructions: string; banner: string; level: number };
  /** Player tried to act with no points left. */
  outOfPoints: void;

  gameOver: { score: number; reason: 'collision'; enemy: EnemyState };
};

export type GameEventName = keyof GameEventMap;
export type Handler<T> = (payload: T) => void;

export class Emitter<Events extends Record<string, unknown>> {
  private readonly handlers = new Map<keyof Events, Set<Handler<unknown>>>();

  on<K extends keyof Events>(event: K, handler: Handler<Events[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<unknown>);
    return () => this.off(event, handler);
  }

  off<K extends keyof Events>(event: K, handler: Handler<Events[K]>): void {
    this.handlers.get(event)?.delete(handler as Handler<unknown>);
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    // Copy so handlers may unsubscribe during dispatch without skipping peers.
    for (const handler of [...set]) (handler as Handler<Events[K]>)(payload);
  }

  clear(): void {
    this.handlers.clear();
  }
}

export type GameEmitter = Emitter<GameEventMap>;
